/**
 * Scheda giocatore (V36) e storico tesseramenti (V37).
 *
 * Generale (gli anelli delle medie di famiglia, anagrafica per
 * l'Admin, card Giocatore e Diffide e squalifiche, note) e tesseramenti stanno su rotte sorelle insieme ad attributi,
 * voti (V38, V39) e trend (V62); le statistiche (V40) stanno nella colonna
 * destra di Generale. Mister e Dirigente non vedono i
 * campi anagrafici, che la testata gia riporta, e cambiano quelli di campo:
 * ruolo, piede, stato, stato sportivo per competizione e note. Il
 * Dirigente vede e scrive gli Attributi, non vede i Voti, e nel Trend ha
 * eventi e attributi senza voti.
 * Il tab Tesseramenti è del solo Admin; il tab Carichi è visibile a chi ha
 * `allenamento.leggi` (Dirigente compreso).
 * La testata ricalca la riga della rosa: la maglia con numero e fascia dello
 * stato, nome con stato e cartellini, e a chi legge le misure le medie di
 * famiglia e l'overall nel tassello a destra.
 */

import { type FormEvent, type ReactNode, useState } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  NotebookPenIcon,
  OctagonXIcon,
  PencilIcon,
  UserIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  ETICHETTA_FAMIGLIA_ATTRIBUTO,
  ETICHETTA_STATO_GIOCATORE,
  ETICHETTA_STATO_SPORTIVO,
  STATI_GIOCATORE,
  STATI_SPORTIVI,
  type StatoGiocatore,
  type StatoSportivo,
  ambitoDelPermesso,
  anagraficaGiocatoreSchema,
  campoGiocatoreSchema,
  noteGiocatoreSchema,
  nuovoTesseramentoSchema,
} from '@calcio/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { chiama } from '../api/client.js';
import { ComandoIcona, MenuAzioni, PulsanteInvio, PulsanteNuovo } from '../comuni/Azioni.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { dataBreve, erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import { IconaFamiglia } from '../comuni/famiglie.js';
import { Maglia } from '../comuni/Maglia.js';
import { type SquadraStagione, useContesto } from '../navigazione/contesto.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import { IconaEvento } from '../partite/eventi.js';
import { type ValoriCampo, valoriCampoDa, valoriDa } from './anagrafica.js';
import { CampiAnagrafica, CampiCampo, CampoCompatto, GRIGLIA_COMPATTA } from './CampiAnagrafica.js';
import { FormTesseramento } from './FormTesseramento.js';
import { AttributiGiocatore } from './AttributiGiocatore.js';
import { StatisticheGiocatore } from './StatisticheGiocatore.js';
import { TrendGiocatore } from './TrendGiocatore.js';
import { VotiGiocatore } from './VotiGiocatore.js';
import { CarichiGiocatore } from '../carichi/CarichiGiocatore.js';
import { COLORE_SPORTIVO, COLORE_STATO } from './colori-stato.js';
import { BadgeStati, BadgeStato, CartellinoSportivo } from './Stati.js';
import { TasselloOverall } from './RigaGiocatore.js';
import { IndicatoriMedie } from './IndicatoriMedie.js';
import {
  type DettaglioGiocatore,
  type FamigliaMedia,
  FAMIGLIE_MEDIA,
  type MedieAttributi,
  type TesseramentoStorico,
  annoDi,
  etichettaRuolo,
  filtriDaSearch,
  percorsoConFiltri,
} from './elenchi.js';

const PIEDI: Record<string, string> = {
  destro: 'Destro',
  sinistro: 'Sinistro',
  ambidestro: 'Ambidestro',
};

function schedaDaPercorso(pathname: string) {
  if (pathname.endsWith('/tesseramenti')) return 'tesseramenti';
  if (pathname.endsWith('/carichi')) return 'carichi';
  if (pathname.endsWith('/attributi')) return 'attributi';
  if (pathname.endsWith('/voti')) return 'voti';
  if (pathname.endsWith('/trend')) return 'trend';
  return 'generale';
}

export function SchedaGiocatore() {
  const { id } = useParams<{ id: string }>();
  const { pathname, search } = useLocation();
  const { utente, squadre, ricaricaSquadre } = useContesto();
  const {
    dati: giocatore,
    errore,
    stato,
    ricarica,
  } = useDati<DettaglioGiocatore>(`/giocatori/${id}`);

  const admin = ambitoDelPermesso(utente.assegnazioni, 'giocatore.gestisci') === 'societa';
  const gestisceTesseramenti =
    ambitoDelPermesso(utente.assegnazioni, 'tesseramento.gestisci') === 'societa';
  const filtri = filtriDaSearch(search);
  const vedeAttributi = (() => {
    const a = ambitoDelPermesso(utente.assegnazioni, 'misurazione.leggi');
    return a === 'societa' || a.length > 0;
  })();
  const vedeVoti = (() => {
    const a = ambitoDelPermesso(utente.assegnazioni, 'voto.leggi');
    return a === 'societa' || a.length > 0;
  })();
  const vedeCarichi = (() => {
    const a = ambitoDelPermesso(utente.assegnazioni, 'allenamento.leggi');
    return a === 'societa' || a.length > 0;
  })();
  const scheda = schedaDaPercorso(pathname);

  // Lo storico dei tesseramenti serve solo all'Admin, che li gestisce.
  if (scheda === 'tesseramenti' && !admin) return <NonTrovata />;
  if (scheda === 'attributi' && !vedeAttributi) return <NonTrovata />;
  if (scheda === 'voti' && !vedeVoti) return <NonTrovata />;
  if (scheda === 'carichi' && !vedeCarichi) return <NonTrovata />;
  if (stato === 404) return <NonTrovata />;
  if (!giocatore)
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );

  const ricaricaTutto = async () => {
    await Promise.all([ricarica(), ricaricaSquadre()]);
  };

  // Le schede si sostituiscono nella cronologia: «indietro» riporta a chi ha
  // aperto il giocatore, rosa o cruscotto, non alla scheda di prima.
  const voci = (
    [
      ['generale', '', 'Generale'],
      admin && ['tesseramenti', '/tesseramenti', 'Tesseramenti'],
      vedeCarichi && ['carichi', '/carichi', 'Carichi'],
      vedeAttributi && ['attributi', '/attributi', 'Attributi'],
      vedeVoti && ['voti', '/voti', 'Voti'],
      ['trend', '/trend', 'Trend'],
    ] as const
  ).filter((v) => v !== false);

  const schede = (
    <Tabs value={scheda}>
      <TabsList className="w-full justify-start overflow-x-auto [scrollbar-width:none]">
        {voci.map(([valore, coda, etichetta]) => (
          <TabsTrigger key={valore} value={valore} asChild>
            <NavLink
              to={percorsoConFiltri(`/giocatori/${giocatore.id}${coda}`, filtri)}
              end={valore === 'generale'}
              replace
            >
              {etichetta}
            </NavLink>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );

  return (
    <div>
      <Testata
        prima={
          <Maglia
            numero={giocatore.numeroMaglia}
            ruolo={giocatore.ruoloPreferito}
            fascia={giocatore.archiviato ? undefined : COLORE_STATO[giocatore.stato]}
            className="ml-1 self-center"
          />
        }
        titolo={`${giocatore.cognome} ${giocatore.nome}`}
        accanto={
          !giocatore.archiviato &&
          (giocatore.stato !== 'disponibile' || giocatore.statiSportivi.length > 0) && (
            <span className="flex flex-wrap items-center gap-2">
              <BadgeStati
                stato={giocatore.stato}
                statiSportivi={giocatore.statiSportivi}
                soloCartellini
              />
            </span>
          )
        }
        sotto={
          <>
            <span className="text-sm text-muted-foreground tabular-nums">
              {[
                annoDi(giocatore.dataNascita),
                PIEDI[giocatore.piede ?? ''],
                etichettaRuolo(giocatore.ruoloPreferito),
              ]
                .filter(Boolean)
                .join(' · ') || '—'}
            </span>
            {giocatore.medieAttributi && (
              <MedieInTestata medie={giocatore.medieAttributi} famiglie={FAMIGLIE_MEDIA} />
            )}
          </>
        }
        dopo={
          giocatore.medieAttributi && <TasselloOverall overall={giocatore.medieAttributi.overall} />
        }
        azione={admin && <Archivia giocatore={giocatore} onCambio={ricaricaTutto} />}
        indietro="/"
      >
        {giocatore.archiviato && (
          <Badge variant="secondary" className="self-start">
            Archiviato
          </Badge>
        )}
      </Testata>

      <Corpo className="flex flex-col gap-6">
        {schede}

        {scheda === 'tesseramenti' && (
          <Tesseramenti
            key={giocatore.id}
            giocatore={giocatore}
            squadre={squadre ?? []}
            gestisce={gestisceTesseramenti}
          />
        )}
        {scheda === 'attributi' && <AttributiGiocatore />}
        {scheda === 'voti' && <VotiGiocatore />}
        {scheda === 'carichi' && <CarichiGiocatore />}
        {scheda === 'trend' && <TrendGiocatore key={giocatore.id} />}
        {scheda === 'generale' && giocatore.medieAttributi && (
          <IndicatoriMedie medie={giocatore.medieAttributi} />
        )}
        {scheda === 'generale' && (
          // Due colonne da `@4xl`: a sinistra il giocatore, a destra le sue
          // statistiche, che decidono la loro disposizione sul proprio spazio.
          <div className="grid gap-8 @4xl:grid-cols-2">
            <Generale
              key={giocatore.id}
              giocatore={giocatore}
              admin={admin}
              onSalvato={ricaricaTutto}
            />
            <div className="flex flex-col @container">
              <StatisticheGiocatore />
            </div>
          </div>
        )}
      </Corpo>
    </div>
  );
}

/** Le medie di famiglia, con le icone e la regola della rosa. */
function MedieInTestata({
  medie,
  famiglie,
}: {
  medie: MedieAttributi;
  famiglie: readonly FamigliaMedia[];
}) {
  return (
    <dl className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
      {famiglie.map((f) => (
        <div
          key={f}
          className="flex items-center gap-1"
          title={`Media attributi ${ETICHETTA_FAMIGLIA_ATTRIBUTO[f].toLowerCase()}`}
        >
          <dt>
            <IconaFamiglia famiglia={f} className="size-4" />
          </dt>
          <dd className="font-medium text-foreground tabular-nums">{medie[f] ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

function Archivia({
  giocatore,
  onCambio,
}: {
  giocatore: DettaglioGiocatore;
  onCambio: () => Promise<void>;
}) {
  const [inCorso, setInCorso] = useState(false);

  const cambia = async () => {
    setInCorso(true);
    try {
      await chiama(`/giocatori/${giocatore.id}/archivio`, {
        metodo: 'POST',
        corpo: { archiviato: !giocatore.archiviato },
      });
      await onCambio();
      toast.success(giocatore.archiviato ? 'Giocatore ripristinato.' : 'Giocatore archiviato.');
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <MenuAzioni
      voci={[
        giocatore.archiviato
          ? {
              etichetta: 'Ripristina',
              Icona: ArchiveRestoreIcon,
              disabled: inCorso,
              onSelect: () => void cambia(),
            }
          : {
              etichetta: 'Archivia',
              Icona: ArchiveIcon,
              disabled: inCorso,
              onSelect: () => void cambia(),
            },
      ]}
    />
  );
}

/** Le voci di una tendina Radix non accettano il valore vuoto. */
const REGOLARE = 'regolare' as const;

function Generale({
  giocatore,
  admin,
  onSalvato,
}: {
  giocatore: DettaglioGiocatore;
  admin: boolean;
  onSalvato: () => Promise<void>;
}) {
  const [valori, setValori] = useState(valoriDa(giocatore));
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [campo, setCampo] = useState(valoriCampoDa(giocatore));
  const [stato, setStato] = useState<StatoGiocatore>(giocatore.stato);
  const [sportivi, setSportivi] = useState(sportiviDa(giocatore));
  const [note, setNote] = useState(giocatore.note ?? '');
  const [salvaNote, setSalvaNote] = useState(false);
  const scrive = giocatore.puoScrivereStati;

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = anagraficaGiocatoreSchema.safeParse(valori);
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama(`/giocatori/${giocatore.id}`, { metodo: 'PUT', corpo: esito.data });
      await onSalvato();
      toast.success('Anagrafica salvata.');
    } catch (errore) {
      setErroreServer(messaggio(errore));
    } finally {
      setInCorso(false);
    }
  };

  // Tendine e note si salvano sulla stessa regola: la scelta si vede subito e
  // torna indietro se il server la rifiuta.
  const salva = async (percorso: string, corpo: object, ripristina: () => void) => {
    try {
      await chiama(`/giocatori/${giocatore.id}/${percorso}`, { metodo: 'PUT', corpo });
      await onSalvato();
    } catch (e) {
      ripristina();
      toast.error(messaggio(e));
    }
  };

  const impostaCampo = (nuovi: ValoriCampo) => {
    setCampo(nuovi);
    void salva('campo', campoGiocatoreSchema.parse(nuovi), () =>
      setCampo(valoriCampoDa(giocatore)),
    );
  };

  const impostaStato = (nuovo: StatoGiocatore) => {
    setStato(nuovo);
    void salva('stato', { stato: nuovo }, () => setStato(giocatore.stato));
  };

  const impostaSportivo = (competizioneId: string, nuovo: StatoSportivo | null) => {
    setSportivi((s) => ({ ...s, [competizioneId]: nuovo }));
    void salva('stato-sportivo', { competizioneId, stato: nuovo }, () =>
      setSportivi(sportiviDa(giocatore)),
    );
  };

  const inviaNote = async (e: FormEvent) => {
    e.preventDefault();
    setSalvaNote(true);
    await salva('note', noteGiocatoreSchema.parse({ note }), () => {});
    setSalvaNote(false);
  };

  // Le attive, piu quelle disattivate dove resta ancora qualcosa da togliere.
  const competizioni = [
    ...giocatore.competizioniAttive,
    ...giocatore.statiSportivi
      .filter((s) => !giocatore.competizioniAttive.some((c) => c.id === s.competizioneId))
      .map((s) => ({ id: s.competizioneId, nome: s.competizione })),
  ];

  return (
    <div className="flex flex-col gap-8">
      {/* Nome, cognome e nascita stanno gia nella testata: a chi non li
          modifica non si ripetono. */}
      {admin && (
        <form onSubmit={(e) => void invia(e)} className="flex max-w-2xl flex-col gap-6">
          <CampiAnagrafica valori={valori} errori={errori} onCambia={setValori} />
          {erroreServer && (
            <Alert variant="destructive">
              <OctagonXIcon />
              <AlertTitle>Salvataggio non riuscito</AlertTitle>
              <AlertDescription>{erroreServer}</AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end">
            <PulsanteInvio type="submit" inCorso={inCorso}>
              Salva
            </PulsanteInvio>
          </div>
        </form>
      )}

      <Sezione titolo="Giocatore" icona={<UserIcon aria-hidden className="size-5" />}>
        {scrive ? (
          <div className={GRIGLIA_COMPATTA}>
            <CampoCompatto id="scheda-stato" etichetta="Stato">
              <TendinaColorata
                id="scheda-stato"
                valore={stato}
                voci={STATI_GIOCATORE.map((s) => [s, ETICHETTA_STATO_GIOCATORE[s]])}
                colori={COLORE_STATO}
                onCambia={impostaStato}
              />
            </CampoCompatto>
            <CampiCampo valori={campo} errori={{}} onCambia={impostaCampo} compatti />
          </div>
        ) : (
          <span className="flex justify-center">
            {giocatore.stato === 'disponibile' ? (
              <span className="text-sm text-muted-foreground">Disponibile</span>
            ) : (
              <BadgeStato stato={giocatore.stato} />
            )}
          </span>
        )}
      </Sezione>

      <Sezione
        titolo="Diffide e squalifiche"
        icona={<IconaEvento tipo="cartellino_giallo" className="size-5" />}
        descrizione="Valgono nella sola competizione in cui sono maturate."
      >
        {competizioni.length === 0 ? (
          <span className="text-center text-sm text-muted-foreground">
            Nessuna competizione attiva.
          </span>
        ) : scrive ? (
          <div className={GRIGLIA_COMPATTA}>
            {competizioni.map((c) => (
              <CampoCompatto key={c.id} id={`scheda-sportivo-${c.id}`} etichetta={c.nome}>
                <TendinaColorata
                  id={`scheda-sportivo-${c.id}`}
                  valore={sportivi[c.id] ?? REGOLARE}
                  voci={[
                    [REGOLARE, 'Regolare'],
                    ...STATI_SPORTIVI.map((s) => [s, ETICHETTA_STATO_SPORTIVO[s]] as const),
                  ]}
                  colori={COLORE_SPORTIVO}
                  onCambia={(v) => impostaSportivo(c.id, v === REGOLARE ? null : v)}
                />
              </CampoCompatto>
            ))}
          </div>
        ) : giocatore.statiSportivi.length === 0 ? (
          <span className="text-center text-sm text-muted-foreground">
            Regolare in tutte le competizioni.
          </span>
        ) : (
          <span className="flex flex-wrap justify-center gap-3">
            {giocatore.statiSportivi.map((s) => (
              <CartellinoSportivo
                key={s.competizioneId}
                stato={s.stato}
                competizione={s.competizione}
              />
            ))}
          </span>
        )}
      </Sezione>

      {/* L'ultima card riempie la colonna: il fondo si allinea alle statistiche. */}
      <Sezione titolo="Note" icona={<NotebookPenIcon aria-hidden className="size-5" />} riempie>
        {scrive ? (
          <form onSubmit={(e) => void inviaNote(e)} className="flex flex-1 flex-col gap-3">
            <Textarea
              id="scheda-note"
              aria-label="Note"
              className="flex-1 bg-card"
              value={note}
              maxLength={2000}
              placeholder="Allergie, indicazioni del medico, accordi con la famiglia…"
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="flex justify-end">
              <PulsanteInvio inCorso={salvaNote} disabled={note.trim() === (giocatore.note ?? '')}>
                Salva note
              </PulsanteInvio>
            </div>
          </form>
        ) : (
          <p className="text-sm whitespace-pre-line">
            {giocatore.note || <span className="text-muted-foreground">Nessuna nota.</span>}
          </p>
        )}
      </Sezione>
    </div>
  );
}

/** Card su sfondo grigio con il titolo in maiuscolo al centro, come i reparti della rosa. */
function Sezione({
  titolo,
  icona,
  descrizione,
  riempie = false,
  children,
}: {
  titolo: string;
  /** A sinistra del titolo, come nelle card di attributi, voti e statistiche. */
  icona: ReactNode;
  descrizione?: string;
  /** Si allunga fino in fondo alla colonna, e il contenuto con lei. */
  riempie?: boolean;
  children: ReactNode;
}) {
  return (
    <Card className={cn('bg-muted', riempie && 'flex-1')}>
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2 font-semibold tracking-wide uppercase">
          {icona}
          <h2>{titolo}</h2>
        </CardTitle>
        {descrizione && <CardDescription>{descrizione}</CardDescription>}
      </CardHeader>
      {/* Le griglie dei campi decidono sulla larghezza della card, non della
          vista: le due card vanno in colonna insieme, prima di tagliarsi. */}
      <CardContent className={cn('@container flex flex-col', riempie && 'flex-1')}>
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * Tendina piccola che mostra il colore di ogni voce con un pallino, anche
 * sulla scelta attiva: lo stato si riconosce senza leggerlo.
 */
function TendinaColorata<V extends string>({
  id,
  valore,
  voci,
  colori,
  onCambia,
}: {
  id: string;
  valore: V;
  voci: readonly (readonly [V, string])[];
  colori: Record<V, string>;
  onCambia: (valore: V) => void;
}) {
  return (
    <Select value={valore} onValueChange={(v) => onCambia(v as V)}>
      <SelectTrigger id={id} size="sm" className="w-40">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {voci.map(([v, etichetta]) => (
            <SelectItem key={v} value={v}>
              <span aria-hidden className={cn('size-2.5 shrink-0 rounded-full', colori[v])} />
              {etichetta}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function sportiviDa(g: DettaglioGiocatore): Record<string, StatoSportivo | null> {
  return Object.fromEntries(g.statiSportivi.map((s) => [s.competizioneId, s.stato]));
}

function Tesseramenti({
  giocatore,
  squadre,
  gestisce,
}: {
  giocatore: DettaglioGiocatore;
  squadre: SquadraStagione[];
  gestisce: boolean;
}) {
  const {
    dati: righe,
    errore,
    ricarica,
  } = useDati<TesseramentoStorico[]>(`/giocatori/${giocatore.id}/tesseramenti`);
  const [aggiunta, setAggiunta] = useState(false);
  const [inModifica, setInModifica] = useState<TesseramentoStorico | null>(null);

  if (!righe) return <StatoCaricamento errore={errore} />;

  const aggiungi = gestisce && (
    <PulsanteNuovo etichetta="Aggiungi tesseramento" onClick={() => setAggiunta(true)} />
  );

  return (
    <div className="flex flex-col gap-4">
      {righe.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Nessun tesseramento</EmptyTitle>
            <EmptyDescription>
              {gestisce
                ? 'Il giocatore non è in nessuna rosa. Aggiungi un tesseramento da oggi.'
                : "I tesseramenti li gestisce l'Admin Squadra della società."}
            </EmptyDescription>
          </EmptyHeader>
          {aggiungi && <EmptyContent>{aggiungi}</EmptyContent>}
        </Empty>
      ) : (
        <>
          <div className="flex justify-end">{aggiungi}</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Squadra</TableHead>
                <TableHead>Stagione</TableHead>
                <TableHead className="text-right">N.</TableHead>
                <TableHead>Periodo</TableHead>
                {gestisce && (
                  <TableHead>
                    <span className="sr-only">Azioni</span>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {righe.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium">{t.squadra}</TableCell>
                  <TableCell>{t.stagione}</TableCell>
                  <TableCell className="text-right tabular-nums">{t.numeroMaglia ?? '—'}</TableCell>
                  <TableCell className="tabular-nums">
                    {dataBreve(t.dataInizio)} – {t.dataFine ? dataBreve(t.dataFine) : 'in corso'}
                  </TableCell>
                  {gestisce && (
                    <TableCell>
                      <div className="flex justify-end">
                        <ComandoIcona
                          etichetta="Modifica il tesseramento"
                          Icona={PencilIcon}
                          size="icon-sm"
                          onClick={() => setInModifica(t)}
                        />
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {gestisce && (
        <>
          <Dialog open={aggiunta} onOpenChange={setAggiunta}>
            <DialogContent>
              {aggiunta && (
                <FormNuovoTesseramento
                  giocatoreId={giocatore.id}
                  squadre={squadre}
                  onChiudi={() => setAggiunta(false)}
                  onAggiunto={async () => {
                    setAggiunta(false);
                    await ricarica();
                  }}
                />
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={inModifica !== null} onOpenChange={(a) => !a && setInModifica(null)}>
            <DialogContent>
              {inModifica && (
                <FormTesseramento
                  key={inModifica.id}
                  ss={inModifica.squadraStagioneId}
                  tesseramentoId={inModifica.id}
                  nome={giocatore.nome}
                  cognome={giocatore.cognome}
                  numeroMaglia={inModifica.numeroMaglia}
                  dataInizio={inModifica.dataInizio}
                  dataFine={inModifica.dataFine}
                  onChiudi={() => setInModifica(null)}
                  onSalvato={async () => {
                    setInModifica(null);
                    await ricarica();
                  }}
                />
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

function FormNuovoTesseramento({
  giocatoreId,
  squadre,
  onChiudi,
  onAggiunto,
}: {
  giocatoreId: string;
  squadre: SquadraStagione[];
  onChiudi: () => void;
  onAggiunto: () => Promise<void>;
}) {
  const [ss, setSs] = useState(squadre[0]?.id ?? '');
  const [numero, setNumero] = useState('');
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const perStagione = new Map<string, SquadraStagione[]>();
  for (const s of squadre) {
    perStagione.set(s.stagione, [...(perStagione.get(s.stagione) ?? []), s]);
  }

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = nuovoTesseramentoSchema.safeParse({
      giocatoreId,
      numeroMaglia: numero === '' ? null : Number(numero),
    });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    if (!ss) return setErrori({ squadraStagioneId: 'Scegli una squadra.' });
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama(`/squadre-stagione/${ss}/tesseramenti`, {
        metodo: 'POST',
        corpo: esito.data,
      });
      await onAggiunto();
      toast.success('Tesseramento aggiunto.');
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  return (
    <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>Aggiungi tesseramento</DialogTitle>
        <DialogDescription>Il tesseramento parte da oggi.</DialogDescription>
      </DialogHeader>

      <FieldGroup>
        <Field data-invalid={Boolean(errori.squadraStagioneId)}>
          <FieldLabel htmlFor="scheda-squadra">Squadra</FieldLabel>
          <Select value={ss} onValueChange={setSs}>
            <SelectTrigger id="scheda-squadra" className="w-full">
              <SelectValue placeholder="Scegli una squadra" />
            </SelectTrigger>
            <SelectContent>
              {[...perStagione].map(([stagione, elenco]) => (
                <SelectGroup key={stagione}>
                  <SelectLabel>{stagione}</SelectLabel>
                  {elenco.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.squadra}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          <FieldError>{errori.squadraStagioneId}</FieldError>
        </Field>
        <Field data-invalid={Boolean(errori.numeroMaglia)}>
          <FieldLabel htmlFor="scheda-numero">Numero di maglia</FieldLabel>
          <Input
            id="scheda-numero"
            type="number"
            inputMode="numeric"
            min={1}
            max={99}
            value={numero}
            aria-invalid={Boolean(errori.numeroMaglia)}
            onChange={(e) => setNumero(e.target.value)}
          />
          <FieldDescription>Facoltativo.</FieldDescription>
          <FieldError>{errori.numeroMaglia}</FieldError>
        </Field>
      </FieldGroup>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso} disabled={!ss}>
          Aggiungi
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}
